import type { NorthStarPayload } from '../types';
import type {
  SituationCard,
  SituationDiagnosisPayload,
  SituationPackPayload,
} from './situationTypes';

const PACK_CAP = 5;

function isShallowMetricCard(card: SituationCard): boolean {
  if (card.evidence?.kind !== 'drill') return false;
  // Diagnosis cards carry explainedBy / contribution; light pack cards do not.
  if (card.explainedBy || card.contribution) return false;
  return Boolean(card.metricKey || card.evidence.metricKey);
}

function metricKeyOf(card: SituationCard): string | null {
  if (card.metricKey) return card.metricKey;
  if (card.evidence?.kind === 'drill') return card.evidence.metricKey;
  return null;
}

/** Walk-ins already arrived — cancel/no-show insights about them are nonsense. */
export function isWalkinRateInsight(card: SituationCard): boolean {
  const metric = metricKeyOf(card) ?? '';
  const blob = [
    card.insight,
    card.why,
    card.explainedBy,
    card.recommendedAction,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const hasWalkin = /walk[\s-]?ins?/.test(blob);
  if (!hasWalkin) return false;
  if (metric === 'cancel' || metric === 'noshow') return true;
  return /\b(cancel|no-?show)/.test(blob);
}

function withoutWalkinRateInsights(cards: SituationCard[]): SituationCard[] {
  return cards.filter(c => !isWalkinRateInsight(c));
}

/**
 * Catalog findings only. North Star stays on its own hero; nuggets never merge.
 */
export function enrichSituationPack(
  pack: SituationPackPayload | undefined,
  opts?: {
    northStar?: NorthStarPayload | null;
    /** @deprecated Ignored — nuggets no longer merge into the brief. */
    heroNuggets?: unknown;
  },
): SituationPackPayload | undefined {
  if (!pack) return pack;
  void opts;
  const kept = withoutWalkinRateInsights(pack.situations);
  const top = kept[0];
  return {
    ...pack,
    situations: kept.slice(0, PACK_CAP),
    headline: top ? top.insight : pack.headline,
  };
}

/**
 * Merge async driver diagnosis into a light pack.
 * Diagnosis cards replace shallow same-metric fleet cards; outlets/NS kept.
 */
export function mergeDiagnosisIntoPack(
  pack: SituationPackPayload | undefined,
  diagnosis: SituationDiagnosisPayload | undefined,
  opts?: { diagnosing?: boolean; diagnosisFailed?: boolean },
): SituationPackPayload | undefined {
  if (!pack) return pack;
  const diagCards = withoutWalkinRateInsights(diagnosis?.situations ?? []);
  const diagnosing = Boolean(opts?.diagnosing);
  const diagnosisFailed = Boolean(opts?.diagnosisFailed);
  const quietReasons = [
    ...(pack.quietReasons ?? []),
    ...(diagnosis?.quietReasons ?? []),
  ].filter((r, i, arr) => arr.indexOf(r) === i);

  const compareLabel =
    diagnosis?.compareLabel ?? pack.compareLabel ?? null;

  if (diagCards.length === 0) {
    const keptPack = withoutWalkinRateInsights(pack.situations);
    const top = keptPack[0];
    return {
      ...pack,
      diagnosing,
      diagnosisFailed,
      compareLabel,
      quietReasons: quietReasons.length ? quietReasons : pack.quietReasons,
      situations: keptPack.slice(0, PACK_CAP),
      headline: top ? top.insight : pack.headline,
    };
  }

  const explainedMetrics = new Set(
    diagCards.map(metricKeyOf).filter((k): k is string => Boolean(k)),
  );

  const kept = withoutWalkinRateInsights(pack.situations).filter(card => {
    // Drop generic steady card when diagnosis has real findings.
    if (card.id === 'perf-steady') return false;
    if (card.evidence?.kind === 'outlet') return true;
    if (card.evidence?.kind === 'northStar') return true;
    const key = metricKeyOf(card);
    if (key && explainedMetrics.has(key) && isShallowMetricCard(card)) {
      return false;
    }
    return true;
  });

  const seen = new Set(kept.map(c => c.id));
  const merged = [
    ...diagCards.filter(c => !seen.has(c.id)),
    ...kept,
  ].sort((a, b) => b.priority - a.priority);

  const top = merged[0];
  return {
    ...pack,
    diagnosing,
    diagnosisFailed: false,
    compareLabel,
    quietReasons: quietReasons.length ? quietReasons : pack.quietReasons,
    situations: merged.slice(0, PACK_CAP),
    headline: top ? top.insight : pack.headline,
    generatedAt: diagnosis?.generatedAt || pack.generatedAt,
  };
}
